import React, { useMemo, useState, useCallback } from 'react';
import dayjs from 'dayjs';
import { ButtonMenu, Checkbox, Icon, IconHighlight, Tooltip, Typography } from '@ann/web-components';
import {
  CardContainer,
  CheckboxWrapper,
  CardMain,
  HeaderRow,
  TitleBlock,
  TitleText,
  NoteIconWrapper,
  HeaderRight,
  Timestamp,
  ContentContainer,
  Description,
  ToggleExpandButton,
  FooterRow,
  FooterLeft,
  AuthorChip,
  Dot,
  CounterChip,
  StatusBadge,
  MenuTriggerWrap,
  ViewFullNoteButton,
} from './NoteCardV2.styled';

const COLLAPSED_TEXT_ESTIMATED_LIMIT = 165;
const MAX_EXPANDED_CHARS = 1200;

const formatTimestamp = (dateValue) => {
  if (!dateValue) return '';

  const parsed = dayjs(dateValue);
  if (!parsed.isValid()) return '';

  if (parsed.isSame(dayjs(), 'day')) {
    return `Today, ${parsed.format('h:mm A')}`;
  }

  return parsed.format('MMM D, h:mm A');
};

const NoteCardV2 = ({ note, isSelected = false, onSelect, onEdit, onPin, onArchiveToggle }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const title = note?.title || 'Untitled';
  const description = note?.description || '';
  const isPinned = Boolean(note?.isPinned);
  const isArchived = String(note?.status || '').toUpperCase() === 'ARCHIVED';
  const timestamp = useMemo(
    () => formatTimestamp(note?.lastUpdated || note?.createdAt),
    [note?.lastUpdated, note?.createdAt]
  );

  const commentCount = useMemo(
    () =>
      Number(
        note?.commentCount ??
          note?.commentsCount ??
          note?.raw?.comments_count ??
          note?.raw?.commentsCount ??
          0
      ),
    [note?.commentCount, note?.commentsCount, note?.raw?.comments_count, note?.raw?.commentsCount]
  );

  const attachmentCount = useMemo(
    () =>
      Number(
        note?.attachmentCount ??
          note?.attachmentsCount ??
          note?.raw?.attachment_count ??
          note?.raw?.attachments_count ??
          note?.raw?.attachmentsCount ??
          0
      ),
    [
      note?.attachmentCount,
      note?.attachmentsCount,
      note?.raw?.attachment_count,
      note?.raw?.attachments_count,
      note?.raw?.attachmentsCount,
    ]
  );

  const linkCount = useMemo(
    () =>
      Number(
        note?.linkCount ??
          note?.link_count ??
          note?.links?.length ??
          note?.raw?.link_count ??
          0
      ),
    [note?.linkCount, note?.link_count, note?.links?.length, note?.raw?.link_count]
  );

  const taskCount = useMemo(
    () =>
      Number(
        note?.taskCount ??
          note?.task_count ??
          note?.tasks?.length ??
          note?.raw?.task_count ??
          0
      ),
    [note?.taskCount, note?.task_count, note?.tasks?.length, note?.raw?.task_count]
  );

  const tasks = note?.tasks ?? [];

  const sentiment = note?.sentiment ?? note?.raw?.sentiment ?? null;
  const delivered = note?.delivered ?? note?.raw?.delivered ?? false;
  const recordingCount = useMemo(
    () =>
      Number(
        note?.recordingCount ??
          note?.recording_count ??
          note?.recordings?.length ??
          note?.raw?.recording_count ??
          0
      ),
    [note?.recordingCount, note?.recording_count, note?.recordings?.length, note?.raw?.recording_count]
  );
  const direction = note?.direction ?? note?.raw?.direction ?? null;
  const callDuration = note?.callDuration ?? note?.call_duration ?? note?.raw?.call_duration ?? null;
  const endedBy = note?.endedBy ?? note?.ended_by ?? note?.call_ended_by ?? note?.raw?.ended_by ?? note?.raw?.call_ended_by ?? null;
  const outcome = note?.outcome ?? note?.raw?.outcome ?? null;

  const shouldShowExpand = description.length > COLLAPSED_TEXT_ESTIMATED_LIMIT;
  const expandedText =
    description.length > MAX_EXPANDED_CHARS ? `${description.slice(0, MAX_EXPANDED_CHARS)}...` : description;

  const handleCardClick = useCallback(() => {
    onEdit?.(note);
  }, [note, onEdit]);

  const handleToggleSelect = useCallback(
    (event) => {
      event?.stopPropagation?.();
      onSelect?.(note?.id);
      // Quitar foco para evitar que el :focus del Checkbox quede "pegado"
      if (document.activeElement) {
        document.activeElement.blur?.();
      }
    },
    [note?.id, onSelect]
  );

  const handleMenuChange = useCallback(
    (action) => {
      if (action === 'edit') {
        onEdit?.(note, { openInEditMode: true });
        return;
      }

      if (action === 'pin' || action === 'unpin') {
        onPin?.(note?.id, action === 'pin');
        return;
      }

      if (action === 'archive' || action === 'unarchive') {
        onArchiveToggle?.(note?.id, action === 'archive');
        return;
      }

      if (action === 'view_details') {
        onEdit?.(note);
      }
    },
    [note, onArchiveToggle, onEdit, onPin]
  );

  const handleToggleExpand = useCallback(
    (event) => {
      event.stopPropagation();
      setIsExpanded((prev) => !prev);
    },
    []
  );
  const handleStopPropagation = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const handleViewFullNote = useCallback((event) => {
    event.stopPropagation();
    onEdit?.(note);
  }, [note, onEdit]);

  return (
    <CardContainer
        onClick={handleCardClick}
        $isSelected={isSelected}
        $isArchived={isArchived}
        data-checkbox-visible={isSelected}
        tabIndex={0}
      >
      <CardMain>
        <HeaderRow>
          <TitleBlock>
            <CheckboxWrapper className="note-card-v2-checkbox" onClick={handleStopPropagation}>
              <Checkbox checked={isSelected} onChange={handleToggleSelect} />
            </CheckboxWrapper>
            <NoteIconWrapper>
              <IconHighlight
                icon="stickyNote2"
                bgColor={isArchived ? 'Neutral' : 'Yellow'}
                iconColor={isArchived ? 'Primary' : 'Yellow'}
                size="small"
              />
            </NoteIconWrapper>

            <TitleText>
              <Typography category="subtitle" size="medium" weight="semibold" color="title" titleOnHover>
                {title}
              </Typography>
            </TitleText>

            {isPinned && !isArchived && (
              <StatusBadge $variant="pinned">
                <Icon name="keep" size="custom" width="16px" height="16px" />
                <Typography category="label" size="small" weight="medium" color="error">
                  Pinned
                </Typography>
              </StatusBadge>
            )}
            {isArchived && (
              <StatusBadge $variant="archived">
                <Icon name="inbox" size="custom" width="16px" height="16px" />
                <Typography category="label" size="small" weight="medium" color="subtitle">
                  Archived
                </Typography>
              </StatusBadge>
            )}
          </TitleBlock>

          <HeaderRight onClick={handleStopPropagation}>
            <Timestamp>
              <Typography category="body" size="small" weight="medium" color="subtitle">
                {timestamp}
              </Typography>
            </Timestamp>
            <MenuTriggerWrap>
              <ButtonMenu
                alignMenu="right"
                customIcon="dotsMenu2"
                iconPosition="right"
                label=""
                menuPosition="bottom"
                onChange={handleMenuChange}
                size="small"
                type="tertiary"
                usePortal={false}
                width="260px"
                disableEffects
                className="note-card-v2-menu"
              >
                <ButtonMenu.Menu icon={true} minWidth="260px">
                  <ButtonMenu.Item label="View details" value="view_details" icon="short_text">
                    View details
                  </ButtonMenu.Item>
                  <ButtonMenu.Item label="Edit" value="edit" icon="edit">
                    Edit
                  </ButtonMenu.Item>
                  <ButtonMenu.Item
                    label={isPinned ? 'Unpin' : 'Pin'}
                    value={isPinned ? 'unpin' : 'pin'}
                    icon={isPinned ? 'keep_off' : 'keep'}
                  >
                    {isPinned ? 'Unpin' : 'Pin'}
                  </ButtonMenu.Item>
                  <ButtonMenu.Item
                    label={isArchived ? 'Unarchive' : 'Archive'}
                    value={isArchived ? 'unarchive' : 'archive'}
                    icon={isArchived ? 'upload' : 'inbox'}
                  >
                    {isArchived ? 'Unarchive' : 'Archive'}
                  </ButtonMenu.Item>
                </ButtonMenu.Menu>
              </ButtonMenu>
            </MenuTriggerWrap>
          </HeaderRight>
        </HeaderRow>

        <ContentContainer $isArchived={isArchived}>
          <Description $isExpanded={isExpanded}>
            <Typography category="body" size="medium" weight="medium" color="subtitle">
              {isExpanded ? expandedText : description}
            </Typography>
          </Description>
          {shouldShowExpand && (
            <ToggleExpandButton onClick={handleToggleExpand} title={isExpanded ? 'Collapse note' : 'Expand note'}>
              <Icon name={isExpanded ? 'chevronUp' : 'chevronDown'} width="20px" height="20px" />
            </ToggleExpandButton>
          )}
        </ContentContainer>

        {isExpanded && description.length > MAX_EXPANDED_CHARS && (
          <ViewFullNoteButton onClick={handleViewFullNote}>
            <Typography category="link" size="small" weight="semibold" color="primary">
              View full note
            </Typography>
          </ViewFullNoteButton>
        )}

        <FooterRow>
          <FooterLeft>
            <AuthorChip $isArchived={isArchived}>
              <Tooltip content="Created by" arrow={false} position="bottom">
                <Typography category="body" size="small" weight="medium" color="subtitle">
                  {note?.owner?.name || 'Unknown'}
                </Typography>
              </Tooltip>
              {commentCount > 0 && (
                <>
                  <Dot />
                  <Tooltip content={`${commentCount} message${commentCount === 1 ? '' : 's'}`} arrow={false} position="bottom">
                    <CounterChip>
                      <Icon name="chat_bubble" size="custom" width="16px" height="16px" />
                      <Typography category="body" size="small" weight="medium" color="subtitle">
                        {String(commentCount)}
                      </Typography>
                    </CounterChip>
                  </Tooltip>
                </>
              )}
              {sentiment && (
                <>
                  <Dot />
                  <Tooltip content={`Sentiment: ${sentiment}`} arrow={false} position="bottom">
                    <CounterChip>
                      <Icon materialName="sentiment_satisfied" size="custom" width="16px" height="16px" />
                    </CounterChip>
                  </Tooltip>
                </>
              )}
              {attachmentCount > 0 && (
                <>
                  <Dot />
                  <Tooltip content="Attachments" arrow={false} position="bottom">
                    <CounterChip>
                      <Icon name="attach_file" size="custom" width="16px" height="16px" />
                      <Typography category="body" size="small" weight="medium" color="subtitle">
                        {String(attachmentCount)}
                      </Typography>
                    </CounterChip>
                  </Tooltip>
                </>
              )}
              {delivered && (
                <>
                  <Dot />
                  <Tooltip content="Delivered" arrow={false} position="bottom">
                    <CounterChip>
                      <Icon name="doneAll" size="custom" width="16px" height="16px" />
                    </CounterChip>
                  </Tooltip>
                </>
              )}
              {taskCount > 0 && (
                <>
                  <Dot />
                  <Tooltip
                    content={
                      <>
                        Assigned task
                        {(tasks.length > 0
                          ? tasks.map((t) => (typeof t === 'string' ? t : t?.title ?? t?.name ?? 'Task'))
                          : Array.from({ length: taskCount }, (_, i) => `Task ${i + 1}`)
                        ).map((label, i) => (
                          <React.Fragment key={`${label}-${i}`}>
                            <br />
                            • {label}
                          </React.Fragment>
                        ))}
                      </>
                    }
                    arrow={false}
                    position="bottom"
                  >
                    <CounterChip>
                      <Icon name="checkbox" size="custom" width="16px" height="16px" />
                      <Typography category="body" size="small" weight="medium" color="subtitle">
                        {String(taskCount)}
                      </Typography>
                    </CounterChip>
                  </Tooltip>
                </>
              )}
              {recordingCount > 0 && (
                <>
                  <Dot />
                  <Tooltip content="Available Recording" arrow={false} position="bottom">
                    <CounterChip>
                      <Icon materialName="graphic_eq" size="custom" width="16px" height="16px" />
                    </CounterChip>
                  </Tooltip>
                </>
              )}
              {direction && (
                <>
                  <Dot />
                  <Tooltip content={`Direction: ${direction}`} arrow={false} position="bottom">
                    <CounterChip>
                      <Icon name="arrowOutward" size="custom" width="16px" height="16px" />
                    </CounterChip>
                  </Tooltip>
                </>
              )}
              {callDuration && (
                <>
                  <Dot />
                  <Tooltip content="Call duration" arrow={false} position="bottom">
                    <CounterChip>
                      <Icon name="timer" size="custom" width="16px" height="16px" />
                      <Typography category="body" size="small" weight="medium" color="subtitle">
                        {String(callDuration)}
                      </Typography>
                    </CounterChip>
                  </Tooltip>
                </>
              )}
              {endedBy && (
                <>
                  <Dot />
                  <Tooltip content={`Call ended by: ${endedBy}`} arrow={false} position="bottom">
                    <CounterChip>
                      <Icon name="call" size="custom" width="16px" height="16px" />
                    </CounterChip>
                  </Tooltip>
                </>
              )}
              {linkCount > 0 && (
                <>
                  <Dot />
                  <Tooltip content="Links" arrow={false} position="bottom">
                    <CounterChip>
                      <Icon name="link" size="custom" width="16px" height="16px" />
                    </CounterChip>
                  </Tooltip>
                </>
              )}
              {outcome && (
                <>
                  <Dot />
                  <Tooltip content={`Outcome: ${outcome}`} arrow={false} position="bottom">
                    <CounterChip>
                      <Icon materialName="flag" size="custom" width="16px" height="16px" />
                    </CounterChip>
                  </Tooltip>
                </>
              )}
            </AuthorChip>
          </FooterLeft>
        </FooterRow>
      </CardMain>
    </CardContainer>
  );
};

export default React.memo(NoteCardV2);
